import { useState, useEffect } from "react";
import { getPartite, createPartita, getFornitori, getMovimenti, deleteMovimento, updateMovimento, getProdotti, bulkDeleteMovimenti } from "@/lib/api";
import { formatDate, formatNumber, getTodayISO } from "@/lib/utils";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, ArrowDownToLine, Package, Edit, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { motion } from "framer-motion";

export default function Entrate() {
  const [entrate, setEntrate] = useState([]);
  const [fornitori, setFornitori] = useState([]);
  const [prodotti, setProdotti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [editingMovimento, setEditingMovimento] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState(false);
  
  const [formData, setFormData] = useState({
    fornitore: "",
    nuovoFornitore: "",
    prodotto: "",
    nuovoProdotto: "",
    quantita_grossa: "",
    quantita_media: "",
    quantita_bolla: "",
    data_entrata: getTodayISO(),
    note: "",
  });

  const [editFormData, setEditFormData] = useState({
    quantita: "",
    quantita_grossa: "",
    quantita_media: "",
    quantita_bolla: "",
    data: "",
    fornitore: "",
    prodotto: "",
    note: "",
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [entrateRes, fornitoriRes, prodottiRes] = await Promise.all([
        getMovimenti({ tipo: "entrata" }),
        getFornitori(),
        getProdotti(),
      ]);
      setEntrate(entrateRes.data);
      setFornitori(fornitoriRes.data);
      setProdotti(prodottiRes.data);
    } catch (error) {
      console.error("Error loading data:", error);
      toast.error("Errore nel caricamento dei dati");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const fornitore = formData.fornitore === "__new__" 
      ? formData.nuovoFornitore 
      : formData.fornitore;
    
    const prodotto = formData.prodotto === "__new__" 
      ? formData.nuovoProdotto 
      : formData.prodotto;
    
    if (!fornitore || !prodotto || !formData.quantita_grossa) {
      toast.error("Compila tutti i campi obbligatori (fornitore, prodotto, quantità grossa)");
      return;
    }

    try {
      await createPartita({
        fornitore,
        prodotto,
        quantita_grossa: parseFloat(formData.quantita_grossa) || 0,
        quantita_media: parseFloat(formData.quantita_media) || 0,
        quantita_bolla: parseFloat(formData.quantita_bolla) || 0,
        data_entrata: formData.data_entrata,
        note: formData.note,
      });
      
      toast.success("Nuova partita registrata con successo");
      setDialogOpen(false);
      setFormData({
        fornitore: "",
        nuovoFornitore: "",
        prodotto: "",
        nuovoProdotto: "",
        quantita_grossa: "",
        quantita_media: "",
        quantita_bolla: "",
        data_entrata: getTodayISO(),
        note: "",
      });
      loadData();
    } catch (error) {
      console.error("Error creating partita:", error);
      toast.error("Errore nella registrazione della partita");
    }
  };

  const handleEditEntrata = (movimento) => {
    setEditingMovimento(movimento);
    setEditFormData({
      quantita: movimento.quantita.toString(),
      data: movimento.data,
      fornitore: movimento.fornitore || "",
      prodotto: movimento.prodotto || "",
      note: movimento.note || "",
    });
    setEditDialogOpen(true);
  };

  const handleSubmitEdit = async (e) => {
    e.preventDefault();
    
    if (!editFormData.quantita) {
      toast.error("La quantità è obbligatoria");
      return;
    }

    try {
      await updateMovimento(editingMovimento.id, {
        partita_id: editingMovimento.partita_id,
        tipo: "entrata",
        quantita: parseFloat(editFormData.quantita),
        data: editFormData.data,
        fornitore: editFormData.fornitore,
        prodotto: editFormData.prodotto,
        note: editFormData.note,
      });
      
      toast.success("Entrata aggiornata con successo");
      setEditDialogOpen(false);
      setEditingMovimento(null);
      loadData();
    } catch (error) {
      console.error("Error updating entrata:", error);
      toast.error(error.response?.data?.detail || "Errore nell'aggiornamento dell'entrata");
    }
  };

  const handleDeleteEntrata = async (id) => {
    try {
      await deleteMovimento(id);
      toast.success("Entrata eliminata con successo");
      setDeleteConfirm(null);
      loadData();
    } catch (error) {
      console.error("Error deleting entrata:", error);
      toast.error("Errore nell'eliminazione dell'entrata");
    }
  };

  const handleBulkDelete = async () => {
    try {
      const response = await bulkDeleteMovimenti(selectedIds);
      toast.success(`Eliminate ${response.data.deleted} entrate`);
      setBulkDeleteConfirm(false);
      setSelectedIds([]);
      loadData();
    } catch (error) {
      console.error("Error bulk deleting:", error);
      toast.error("Errore nell'eliminazione");
    }
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === entrate.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(entrate.map(e => e.id));
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const totaleEntrate = entrate.reduce((sum, e) => sum + (e.quantita || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div data-testid="entrate-page" className="w-full space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl lg:text-3xl font-bold text-slate-900 tracking-tight">
            Entrate
          </h1>
          <p className="text-slate-500 text-sm mt-0.5">
            Registra nuove partite in ingresso
          </p>
        </div>
        
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button 
              data-testid="nuova-entrata-btn"
              className="btn-accent rounded-lg"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nuova Entrata
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl font-bold flex items-center gap-2">
                <ArrowDownToLine className="w-5 h-5 text-orange-500" />
                Registra Nuova Partita
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="fornitore" className="font-heading font-bold text-slate-700">
                  Fornitore *
                </Label>
                <Select
                  value={formData.fornitore}
                  onValueChange={(value) => setFormData({ ...formData, fornitore: value })}
                >
                  <SelectTrigger data-testid="fornitore-select" className="rounded-lg">
                    <SelectValue placeholder="Seleziona fornitore" />
                  </SelectTrigger>
                  <SelectContent>
                    {fornitori.map((f) => (
                      <SelectItem key={f.id} value={f.nome}>
                        {f.nome}
                      </SelectItem>
                    ))}
                    <SelectItem value="__new__">+ Nuovo Fornitore</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {formData.fornitore === "__new__" && (
                <div className="space-y-2">
                  <Label htmlFor="nuovoFornitore" className="font-heading font-bold text-slate-700">
                    Nome Nuovo Fornitore *
                  </Label>
                  <Input
                    id="nuovoFornitore"
                    data-testid="nuovo-fornitore-input"
                    value={formData.nuovoFornitore}
                    onChange={(e) => setFormData({ ...formData, nuovoFornitore: e.target.value })}
                    placeholder="Es: Ditta Rossi"
                    className="rounded-sm"
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="prodotto" className="font-heading font-bold text-slate-700">
                  Prodotto *
                </Label>
                <Select
                  value={formData.prodotto}
                  onValueChange={(value) => setFormData({ ...formData, prodotto: value })}
                >
                  <SelectTrigger data-testid="prodotto-select" className="rounded-sm">
                    <SelectValue placeholder="Seleziona prodotto" />
                  </SelectTrigger>
                  <SelectContent>
                    {prodotti.map((p) => (
                      <SelectItem key={p.id} value={p.nome}>
                        {p.nome}
                      </SelectItem>
                    ))}
                    <SelectItem value="__new__">+ Nuovo Prodotto</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {formData.prodotto === "__new__" && (
                <div className="space-y-2">
                  <Label htmlFor="nuovoProdotto" className="font-heading font-bold text-slate-700">
                    Nome Nuovo Prodotto *
                  </Label>
                  <Input
                    id="nuovoProdotto"
                    data-testid="nuovo-prodotto-input"
                    value={formData.nuovoProdotto}
                    onChange={(e) => setFormData({ ...formData, nuovoProdotto: e.target.value })}
                    placeholder="Es: Fanghi industriali"
                    className="rounded-sm"
                  />
                </div>
              )}

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="quantita_grossa" className="font-heading font-bold text-slate-700">
                    Quantità Grossa (kg) *
                  </Label>
                  <Input
                    id="quantita_grossa"
                    data-testid="quantita-grossa-input"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.quantita_grossa}
                    onChange={(e) => setFormData({ ...formData, quantita_grossa: e.target.value })}
                    placeholder="0.00"
                    className="rounded-sm font-mono"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="quantita_media" className="font-heading font-bold text-slate-700">
                    Quantità Media (kg)
                  </Label>
                  <Input
                    id="quantita_media"
                    data-testid="quantita-media-input"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.quantita_media}
                    onChange={(e) => setFormData({ ...formData, quantita_media: e.target.value })}
                    placeholder="0.00 (opzionale)"
                    className="rounded-sm font-mono"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="quantita_bolla" className="font-heading font-bold text-slate-700">
                    Quantità Bolla (kg)
                  </Label>
                  <Input
                    id="quantita_bolla"
                    data-testid="quantita-bolla-input"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.quantita_bolla}
                    onChange={(e) => setFormData({ ...formData, quantita_bolla: e.target.value })}
                    placeholder="0.00 (opzionale)"
                    className="rounded-sm font-mono"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="data" className="font-heading font-bold text-slate-700">
                    Data Entrata *
                  </Label>
                  <Input
                    id="data"
                    data-testid="data-input"
                    type="date"
                    value={formData.data_entrata}
                    onChange={(e) => setFormData({ ...formData, data_entrata: e.target.value })}
                    className="rounded-sm font-mono"
                  />
                </div>
              </div>

              {(formData.quantita_grossa || formData.quantita_media || formData.quantita_bolla) && (
                <div className="p-3 bg-slate-50 rounded-sm">
                  <p className="text-sm text-slate-500">Totale Entrata:</p>
                  <p className="font-mono font-bold text-lg text-emerald-600">
                    {formatNumber((parseFloat(formData.quantita_grossa) || 0) + (parseFloat(formData.quantita_media) || 0) + (parseFloat(formData.quantita_bolla) || 0))} kg
                  </p>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="note" className="font-heading font-bold text-slate-700">
                  Note
                </Label>
                <Textarea
                  id="note"
                  data-testid="note-input"
                  value={formData.note}
                  onChange={(e) => setFormData({ ...formData, note: e.target.value })}
                  placeholder="Note aggiuntive..."
                  className="rounded-sm"
                  rows={3}
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setDialogOpen(false)}
                  className="rounded-sm"
                >
                  Annulla
                </Button>
                <Button 
                  type="submit" 
                  data-testid="submit-entrata-btn"
                  className="btn-primary rounded-sm"
                >
                  Registra Partita
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Summary Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <Card className="kpi-card border-l-4 border-emerald-500">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500 uppercase tracking-wide">
                  Totale Entrate Registrate
                </p>
                <p className="font-heading text-3xl font-bold text-slate-900 mt-2">
                  {formatNumber(totaleEntrate)} kg
                </p>
                <p className="text-sm text-slate-500 mt-1">
                  {entrate.length} movimenti
                </p>
              </div>
              <div className="w-16 h-16 rounded-sm bg-emerald-100 flex items-center justify-center">
                <ArrowDownToLine className="w-8 h-8 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        <Card className="card-industrial">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="font-heading text-xl font-bold text-slate-900 flex items-center gap-2">
                <Package className="w-5 h-5 text-orange-500" />
                Storico Entrate
              </CardTitle>
              {selectedIds.length > 0 && (
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => setBulkDeleteConfirm(true)}
                  className="h-8"
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Elimina {selectedIds.length}
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {entrate.length > 0 ? (
              <div className="table-scroll-wrapper">
                <div className="rounded-sm border border-slate-200 overflow-hidden">
                  <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="w-10 text-center">
                        <Checkbox
                          checked={selectedIds.length === entrate.length && entrate.length > 0}
                          onCheckedChange={toggleSelectAll}
                        />
                      </TableHead>
                      <TableHead className="font-heading font-bold text-xs uppercase tracking-wider text-slate-500">
                        Codice Partita
                      </TableHead>
                      <TableHead className="font-heading font-bold text-xs uppercase tracking-wider text-slate-500">
                        Data
                      </TableHead>
                      <TableHead className="font-heading font-bold text-xs uppercase tracking-wider text-slate-500">
                        Fornitore
                      </TableHead>
                      <TableHead className="font-heading font-bold text-xs uppercase tracking-wider text-slate-500">
                        Prodotto
                      </TableHead>
                      <TableHead className="font-heading font-bold text-xs uppercase tracking-wider text-slate-500 text-right">
                        Grossa
                      </TableHead>
                      <TableHead className="font-heading font-bold text-xs uppercase tracking-wider text-slate-500 text-right">
                        Media
                      </TableHead>
                      <TableHead className="font-heading font-bold text-xs uppercase tracking-wider text-slate-500 text-right">
                        Bolla
                      </TableHead>
                      <TableHead className="font-heading font-bold text-xs uppercase tracking-wider text-slate-500 text-right">
                        Totale
                      </TableHead>
                      <TableHead className="font-heading font-bold text-xs uppercase tracking-wider text-slate-500 text-center">
                        Azioni
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {entrate.map((entrata, index) => (
                      <TableRow 
                        key={entrata.id || index}
                        data-testid={`entrata-row-${index}`}
                        className={`hover:bg-slate-50/50 ${selectedIds.includes(entrata.id) ? 'bg-orange-50' : ''}`}
                      >
                        <TableCell className="text-center">
                          <Checkbox
                            checked={selectedIds.includes(entrata.id)}
                            onCheckedChange={() => toggleSelect(entrata.id)}
                          />
                        </TableCell>
                        <TableCell className="font-mono text-sm font-medium text-slate-900">
                          {entrata.codice_partita}
                        </TableCell>
                        <TableCell className="font-mono text-sm text-slate-600">
                          {formatDate(entrata.data)}
                        </TableCell>
                        <TableCell className="text-sm text-slate-700">
                          {entrata.fornitore}
                        </TableCell>
                        <TableCell className="text-sm text-slate-700">
                          {entrata.prodotto}
                        </TableCell>
                        <TableCell className="font-mono text-sm text-slate-600 text-right">
                          {formatNumber(entrata.quantita_grossa || 0)} kg
                        </TableCell>
                        <TableCell className="font-mono text-sm text-slate-600 text-right">
                          {formatNumber(entrata.quantita_media || 0)} kg
                        </TableCell>
                        <TableCell className="font-mono text-sm text-slate-600 text-right">
                          {formatNumber(entrata.quantita_bolla || 0)} kg
                        </TableCell>
                        <TableCell className="font-mono text-sm font-bold text-emerald-600 text-right">
                          +{formatNumber(entrata.quantita)} kg
                        </TableCell>
                        <TableCell className="text-center">
                          <div className="flex items-center justify-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              data-testid={`edit-entrata-${index}`}
                              onClick={() => handleEditEntrata(entrata)}
                              className="h-8 w-8 p-0"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              data-testid={`delete-entrata-${index}`}
                              onClick={() => setDeleteConfirm(entrata)}
                              className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
            ) : (
              <div className="text-center py-12 text-slate-400">
                <ArrowDownToLine className="w-16 h-16 mx-auto mb-4 opacity-30" />
                <p className="text-lg font-medium">Nessuna entrata registrata</p>
                <p className="text-sm mt-1">Clicca su "Nuova Entrata" per iniziare</p>
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>

      {/* Dialog Modifica Entrata */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold flex items-center gap-2">
              <Edit className="w-5 h-5 text-orange-500" />
              Modifica Entrata
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmitEdit} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label className="font-heading font-bold text-slate-700">Fornitore</Label>
              <Input
                data-testid="edit-fornitore-input"
                value={editFormData.fornitore}
                onChange={(e) => setEditFormData({ ...editFormData, fornitore: e.target.value })}
                className="rounded-sm"
              />
            </div>

            <div className="space-y-2">
              <Label className="font-heading font-bold text-slate-700">Prodotto</Label>
              <Input
                data-testid="edit-prodotto-input"
                value={editFormData.prodotto}
                onChange={(e) => setEditFormData({ ...editFormData, prodotto: e.target.value })}
                className="rounded-sm"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="font-heading font-bold text-slate-700">Quantità (kg) *</Label>
                <Input
                  data-testid="edit-quantita-entrata-input"
                  type="number"
                  step="0.01"
                  min="0"
                  value={editFormData.quantita}
                  onChange={(e) => setEditFormData({ ...editFormData, quantita: e.target.value })}
                  className="rounded-sm font-mono"
                />
              </div>
              <div className="space-y-2">
                <Label className="font-heading font-bold text-slate-700">Data</Label>
                <Input
                  data-testid="edit-data-entrata-input"
                  type="date"
                  value={editFormData.data}
                  onChange={(e) => setEditFormData({ ...editFormData, data: e.target.value })}
                  className="rounded-sm font-mono"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="font-heading font-bold text-slate-700">Note</Label>
              <Textarea
                data-testid="edit-note-entrata-input"
                value={editFormData.note}
                onChange={(e) => setEditFormData({ ...editFormData, note: e.target.value })}
                className="rounded-sm"
                rows={2}
              />
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)} className="rounded-sm">
                Annulla
              </Button>
              <Button type="submit" data-testid="save-edit-entrata-btn" className="btn-primary rounded-sm">
                Salva Modifiche
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Conferma Eliminazione</AlertDialogTitle>
            <AlertDialogDescription>
              Sei sicuro di voler eliminare questa entrata?
              <br />
              La quantità verrà sottratta dalla partita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Annulla</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-delete-entrata-btn"
              onClick={() => handleDeleteEntrata(deleteConfirm?.id)}
              className="bg-red-600 hover:bg-red-700 rounded-sm"
            >
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Delete Confirmation */}
      <AlertDialog open={bulkDeleteConfirm} onOpenChange={setBulkDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-red-600">Eliminazione Multipla</AlertDialogTitle>
            <AlertDialogDescription>
              Stai per eliminare <span className="font-bold">{selectedIds.length} entrate</span>.
              <br /><br />
              <span className="text-red-600 font-medium">Questa azione non può essere annullata!</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Annulla</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBulkDelete}
              className="bg-red-600 hover:bg-red-700 rounded-sm"
            >
              Elimina {selectedIds.length} entrate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
